import React, { useEffect, useRef, useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import { FaTrash } from 'react-icons/fa6';
import { IoMdClose } from 'react-icons/io';
import { useBetContext } from '../../../../context/BetContext.tsx';
import { useUser } from '../../../../context/UserContext.tsx';
import { useModal } from '../../../../context/ModalContext.tsx';
import { BetDetails, PlaceWagersResult } from '../../../../models/MyBetModel.tsx';
import { Odd } from '../../../../models/Odd_response_model.tsx';
import WagerConfirmedSlip from '../WagerConfirmedSlip.tsx';
import {
    calcProfit,
    formatCurrency,
    formatNumber,
    formatBetLine,
    getDisplayBetInfo,
    shouldShowDelayedBetMessage,
} from '../../../../helpers/functions.tsx';
import {
    buildParlayPriceEntries,
    parseParlayPriceResponse,
    parlayPrice,
    placeApiAllWagers,
    resolveParlaySportsbooks,
} from '../../../../services/player/apis/Schelude.tsx';
import { logUserActivity } from '../../../../services/player/LoginService.tsx';
import '../../../../styles/BetSlip.css';
import '../../../../styles/PropsBetSlip.css';

interface PropsBetSlipProps {
    setShowLimitAlert?: (value: boolean) => void;
    setErrorMessage?: (value: string) => void;
}

const propLineTitle = (bet: BetDetails) => {
    const odd = bet.odd;
    const market = bet.marketName || odd?.market || '';
    const selection = String(odd?.selection ?? '').trim();
    const player = String(odd?.name || bet.betName || '').trim();
    if (selection && selection.toLowerCase() !== player.toLowerCase()) {
        return market && !selection.toLowerCase().includes(market.toLowerCase())
            ? `${selection} ${market}`
            : selection;
    }
    const side = String(odd?.selection_line ?? '').trim();
    const sideLabel = side ? side.charAt(0).toUpperCase() + side.slice(1).toLowerCase() : '';
    const points = odd?.points != null ? String(odd.points) : '';
    return [sideLabel, points, market].filter(Boolean).join(' ');
};

const propPointLabel = (odd?: Odd | null) => {
    if (!odd || odd.points == null) return '';
    const line = String(odd.selection_line ?? '').toLowerCase();
    const prefix = line.startsWith('over') ? 'O' : line.startsWith('under') ? 'U' : '';
    return [prefix, odd.points].filter(v => v !== '').join(' ');
};

const formatOdds = (price?: number) => {
    if (price == null || Number.isNaN(price)) return '—';
    return formatNumber(price);
};

const PropsBetSlip = ({ setShowLimitAlert, setErrorMessage }: PropsBetSlipProps) => {
    const { bets, clearBets, deleteBet, updateBet, useFreePlay, setUseFreePlay } = useBetContext();
    const { user, updateUser, userIpData } = useUser();
    const { closeModal } = useModal();

    const [parlayStake, setParlayStake] = useState('');
    const [parlayReward, setParlayReward] = useState(0);
    const [priceParlay, setPriceParlay] = useState<{ price: number } | null>(null);
    const [parlayError, setParlayError] = useState<string | null>(null);
    const [isLoadingParlayPrice, setIsLoadingParlayPrice] = useState(false);
    const [placingParlay, setPlacingParlay] = useState(false);
    const [placingSingles, setPlacingSingles] = useState(false);
    const [showPendingOnly, setShowPendingOnly] = useState(false);
    const [userBet, setUserBet] = useState<BetDetails[]>([]);
    const [ticketNumber, setTicketNumber] = useState<number | null>(null);
    const [confirmedKind, setConfirmedKind] = useState<'straight' | 'parlay'>('straight');
    const [confirmedOdds, setConfirmedOdds] = useState<string | null>(null);
    const previousValidBets = useRef<string | null>(null);
    const betsRef = useRef(bets);
    betsRef.current = bets;
    const canParlay = bets.length >= 2;
    const shouldShowDelayedMessage = bets.some(bet => shouldShowDelayedBetMessage(bet));
    const singlesWithStake = bets.filter(b => Number(b.bet) > 0);
    const betsPriceKey = bets
        .map(b => `${b.fixture_id}|${b.odd?.market || b.marketName}|${b.odd?.name || b.betName}|${b.id_selection}|${b.odd?.points ?? ''}|${b.odd?.sportsbook ?? ''}`)
        .join('||');

    useEffect(() => {
        setParlayError(null);
        setPriceParlay(null);

        if (!canParlay) {
            previousValidBets.current = null;
            setIsLoadingParlayPrice(false);
            setParlayStake('');
            setParlayReward(0);
            return;
        }

        const validBets = buildParlayPriceEntries(betsRef.current);
        const sportsbooks = resolveParlaySportsbooks(betsRef.current);

        if (validBets.length < 2) {
            setParlayError('One or more selections are missing required data. Please reselect your picks.');
            setIsLoadingParlayPrice(false);
            return;
        }

        const validBetsKey = [
            sportsbooks.join(','),
            ...validBets.map(b => `${b.fixture_id}-${b.market}-${b.name}`).sort(),
        ].join('|');

        previousValidBets.current = validBetsKey;
        let cancelled = false;
        setIsLoadingParlayPrice(true);

        const fetchPrice = async () => {
            try {
                const response = await parlayPrice(validBets, sportsbooks);
                if (cancelled || previousValidBets.current !== validBetsKey) return;

                const parsed = parseParlayPriceResponse(response, sportsbooks);
                if (!parsed.ok) {
                    setParlayError(parsed.message);
                    setPriceParlay(null);
                    return;
                }

                setParlayError(null);
                setPriceParlay({ price: parsed.price });
            } catch (error) {
                if (cancelled || previousValidBets.current !== validBetsKey) return;
                console.error(error);
                setParlayError('Unable to calculate parlay at this time. Please check your connection and try again.');
                setPriceParlay(null);
            } finally {
                if (!cancelled && previousValidBets.current === validBetsKey) {
                    setIsLoadingParlayPrice(false);
                }
            }
        };

        void fetchPrice();
        return () => { cancelled = true; };
    }, [betsPriceKey, canParlay]);

    const calculateParlayReward = (betAmount: number) => {
        if (!priceParlay?.price) return 0;
        const priceTotal = priceParlay.price;
        if (priceTotal > 0) return Math.round((priceTotal / 100) * betAmount);
        return Math.round((100 / Math.abs(priceTotal)) * betAmount);
    };

    useEffect(() => {
        const numericValue = parlayStake === '' ? 0 : parseFloat(parlayStake);
        setParlayReward(calculateParlayReward(Number.isNaN(numericValue) ? 0 : numericValue));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [priceParlay, parlayStake]);

    const handleParlayStakeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let value = event.target.value;
        if (value.startsWith('0') && value.length > 1 && !value.startsWith('0.')) {
            value = value.replace(/^0+/, '');
        }
        if (/^\d*\.?\d*$/.test(value)) {
            setParlayStake(value);
        }
    };

    const updateSingleStake = (bet: BetDetails, rawValue: string) => {
        const price = bet.odd?.price ?? 0;
        if (rawValue === '' || !/^\d*\.?\d*$/.test(rawValue)) {
            updateBet({ ...bet, bet: 0, reward: 0 });
            return;
        }
        const stake = parseFloat(rawValue);
        if (Number.isNaN(stake) || stake < 0) return;
        const rounded = Math.round(stake);
        updateBet({
            ...bet,
            bet: rounded,
            reward: calcProfit(rounded, price),
        });
    };

    const getLatestGameDate = (list: BetDetails[]) => {
        if (!list.length) return Timestamp.fromDate(new Date());
        return list.reduce((latest, current) => {
            if (!current.gameDate) return latest;
            return current.gameDate.toMillis() > latest.toMillis()
                ? current.gameDate
                : latest;
        }, list[0].gameDate || Timestamp.fromDate(new Date()));
    };

    const showResult = (
        placedBets: BetDetails[],
        ticketFromResult: string | null | undefined,
        kind: 'straight' | 'parlay',
        odds: string | null,
    ) => {
        setUserBet(placedBets);
        setTicketNumber(
            ticketFromResult
                ? Number(String(ticketFromResult).split('-')[0]) || null
                : null
        );
        setConfirmedKind(kind);
        setConfirmedOdds(odds);
        setShowPendingOnly(true);
        clearBets();
        setParlayStake('');
        setParlayReward(0);
        setUseFreePlay?.(false);
    };

    const placeParlay = async () => {
        if (!user?.id) {
            setShowLimitAlert?.(true);
            setErrorMessage?.('Please login to place a bet.');
            return;
        }
        if (!canParlay || !priceParlay || parlayError || isLoadingParlayPrice) {
            setShowLimitAlert?.(true);
            setErrorMessage?.(parlayError ?? 'Parlay odds are not available yet. Please wait or try again.');
            return;
        }

        const numericTotalBet = parseFloat(parlayStake) || 0;
        if (numericTotalBet <= 0) {
            setShowLimitAlert?.(true);
            setErrorMessage?.('Enter a parlay wager amount.');
            return;
        }

        setPlacingParlay(true);
        try {
            const uniqueGameIds = [...new Set(
                bets.map(b => b.gameId).filter(Boolean).map(id => String(id).trim())
            )];
            const sanitizedSelections = bets.map(({ odds, newOdd, ...selection }: any) => selection);
            const parlayDetails: BetDetails = {
                bet: numericTotalBet,
                reward: Number(parlayReward.toFixed(2)),
                betStatus: 'pending',
                betDate: Timestamp.fromDate(new Date()),
                betType: 'Parlay',
                description: `Prop Bet Parlay`,
                gameDate: getLatestGameDate(bets),
                selections: sanitizedSelections,
                gameId: uniqueGameIds[0] || '',
            };

            const result: PlaceWagersResult = await placeApiAllWagers(user.id, [parlayDetails], false);

            if (result?.hasErrors) {
                const firstError = result?.errors?.[0]?.error;
                setShowLimitAlert?.(true);
                setErrorMessage?.(firstError?.message ?? 'There was an error placing the parlay.');
                return;
            }

            const placedBets = (result?.bets?.length ? result.bets : [parlayDetails]) as BetDetails[];
            const placedBet = placedBets[0];
            const totalStake = Number(placedBet.bet ?? numericTotalBet);
            const ticketFromResult = placedBet.ticket ?? null;

            updateUser({
                available: parseFloat(((user.available ?? 0) - totalStake).toFixed(2)),
            });
            logUserActivity(
                user.userName,
                `Ticket: ${ticketFromResult ?? ''} Risk: $${totalStake.toFixed(2)} Win: $${Number(placedBet.reward ?? parlayReward).toFixed(2)}`,
                userIpData
            );
            showResult(
                placedBets,
                ticketFromResult,
                'parlay',
                priceParlay?.price != null ? formatNumber(priceParlay.price) : null,
            );
        } catch (error) {
            console.error(error);
            setShowLimitAlert?.(true);
            setErrorMessage?.('There was an error confirming the parlay. Please try again.');
        } finally {
            setPlacingParlay(false);
        }
    };

    const placeSingles = async () => {
        if (!user?.id || !user.parent) {
            setShowLimitAlert?.(true);
            setErrorMessage?.('Please login to place a bet.');
            return;
        }
        if (singlesWithStake.length === 0) {
            setShowLimitAlert?.(true);
            setErrorMessage?.('Enter a wager amount on at least one single.');
            return;
        }

        setPlacingSingles(true);
        try {
            const sanitizedBets = singlesWithStake.map(({ odds, newOdd, ...bet }: any) => ({
                ...bet,
                betType: bet.betType || 'Straight',
            }));
            const result: PlaceWagersResult = await placeApiAllWagers(user.id, sanitizedBets, useFreePlay);

            if (result?.hasErrors) {
                const firstError = result?.errors?.[0]?.error;
                setShowLimitAlert?.(true);
                setErrorMessage?.(firstError?.message ?? 'There was an error placing one or more bets.');
                return;
            }

            const placedBets = (result?.bets?.length ? result.bets : sanitizedBets) as BetDetails[];
            const totalStake = placedBets.reduce((sum, b) => sum + Number(b.bet ?? 0), 0);
            const ticketFromResult = placedBets.find(b => b.ticket)?.ticket ?? null;

            if (useFreePlay) {
                updateUser({
                    freePlay: parseFloat(((user.freePlay ?? 0) - totalStake).toFixed(2)),
                });
            } else {
                updateUser({
                    available: parseFloat(((user.available ?? 0) - totalStake).toFixed(2)),
                });
            }

            for (const placedBet of placedBets) {
                logUserActivity(
                    user.userName,
                    `Ticket: ${placedBet.ticket ?? ticketFromResult ?? ''} Risk: $${Number(placedBet.bet ?? 0).toFixed(2)} Win: $${Number(placedBet.reward ?? 0).toFixed(2)}`,
                    userIpData
                );
            }

            showResult(placedBets, ticketFromResult, 'straight', null);
        } catch (error) {
            console.error(error);
            setShowLimitAlert?.(true);
            setErrorMessage?.('Error placing bets.');
        } finally {
            setPlacingSingles(false);
        }
    };

    const resetAndClose = () => {
        setShowPendingOnly(false);
        setTicketNumber(null);
        setUserBet([]);
        setConfirmedOdds(null);
        setConfirmedKind('straight');
        setUseFreePlay?.(false);
        closeModal();
    };

    const handleClearAll = () => {
        clearBets();
        setParlayStake('');
        setParlayReward(0);
        closeModal();
    };

    useEffect(() => {
        if (showPendingOnly || placingParlay || placingSingles) return;
        if (bets.length === 0) closeModal();
    }, [bets.length, showPendingOnly, placingParlay, placingSingles, closeModal]);

    if (!user) return null;

    if (showPendingOnly) {
        const isParlay = confirmedKind === 'parlay';
        const legs = isParlay
            ? userBet.flatMap((item) => (item.selections?.length ? item.selections : [item]))
            : userBet;
        const pendingRisk = userBet.reduce((acc, b) => acc + Number(b.bet), 0);
        const pendingReward = userBet.reduce((acc, b) => acc + (b.reward ?? 0), 0);

        return (
            <div className="props-bet-slip props-bet-slip--confirmed">
                <WagerConfirmedSlip
                    legs={legs}
                    risk={pendingRisk}
                    win={pendingReward}
                    ticketNumber={ticketNumber}
                    onClose={resetAndClose}
                    kind={confirmedKind}
                    oddsLabel={confirmedOdds}
                    showLegAmounts={!isParlay && legs.length > 1}
                />
            </div>
        );
    }

    return (
        <div className="props-bet-slip">
            <div className="props-bet-slip__header">
                <div className="props-bet-slip__header-left">
                    <span className="props-bet-slip__count">{bets.length || userBet.length}</span>
                    <h1 className="props-bet-slip__title teko">Bet Slip</h1>
                </div>
                <button type="button" className="props-bet-slip__close" onClick={resetAndClose} aria-label="Close">
                    <IoMdClose size={20} />
                </button>
            </div>

            {!showPendingOnly && (
                <>
                    <div className="props-bet-slip__toolbar">
                        <span className="props-bet-slip__section-label">
                            Singles ({bets.length}) Parlays ({canParlay ? 1 : 0})
                        </span>
                        <button type="button" className="props-bet-slip__clear"
                            onClick={handleClearAll}>
                            Clear All <FaTrash />
                        </button>
                    </div>

                    <div className="props-bet-slip__body">
                        {canParlay && (
                            <div className="props-bet-slip__parlay">
                                <div className="props-bet-slip__parlay-header">
                                    <span>{bets.length} Leg Parlay</span>
                                    <span className="props-bet-slip__odds-pill">
                                        @ {isLoadingParlayPrice ? '...' : (priceParlay ? formatOdds(priceParlay.price) : '—')}
                                    </span>
                                </div>
                                <div className="props-bet-slip__parlay-body">
                                    <div className={`props-bet-slip__parlay-status ${parlayError ? 'is-error' : ''}`} aria-live="polite">
                                        {parlayError
                                            ? parlayError
                                            : isLoadingParlayPrice
                                                ? 'Calculating parlay odds...'
                                                : '\u00A0'}
                                    </div>
                                    <div className="props-bet-slip__stake-row">
                                        <span className="props-bet-slip__currency">$</span>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            className="props-bet-slip__stake-input"
                                            value={parlayStake}
                                            onChange={handleParlayStakeChange}
                                            placeholder="0.00"
                                        />
                                    </div>
                                    <div className="props-bet-slip__return">
                                        Return: {parlayStake && priceParlay ? `$${formatCurrency(parlayReward)}` : '—'}
                                    </div>
                                </div>
                                <div className="props-bet-slip__parlay-actions">
                                    <button
                                        type="button"
                                        className="props-bet-slip__btn props-bet-slip__btn--primary"
                                        onClick={() => { void placeParlay(); }}
                                        disabled={
                                            placingParlay ||
                                            placingSingles ||
                                            !!parlayError ||
                                            !priceParlay ||
                                            isLoadingParlayPrice ||
                                            !(parseFloat(parlayStake) > 0)
                                        }
                                    >
                                        {placingParlay ? 'Please wait...' : 'Place Parlay'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {bets.map((bet) => {
                            const display = getDisplayBetInfo(bet);
                            const matchup = [bet.teamId, bet.opposingTeam].filter(Boolean).join(' @ ');
                            const playerName = display.name || bet.odd?.name || bet.betName || '';
                            const lineTitle = propLineTitle(bet);
                            const pointLabel = propPointLabel(bet.odd);
                            const oddsList: Odd[] = bet.odds && bet.odds.length > 0
                                ? bet.odds
                                : bet.odd ? [bet.odd] : [];
                            const canBuyPoints = oddsList.length > 1;
                            const applyOdd = (newOdd: Odd) => {
                                const stake = Number(bet.bet) || 0;
                                updateBet({
                                    ...bet,
                                    odd: newOdd,
                                    betName: newOdd.name || bet.betName,
                                    odds: oddsList,
                                    reward: calcProfit(stake, newOdd.price ?? 0),
                                });
                            };
                            return (
                                <div className="props-bet-slip__single" key={bet.id_selection}>
                                    <div className="props-bet-slip__single-top">
                                        <div className="props-bet-slip__single-name">{playerName}</div>
                                        <button type="button" className="props-bet-slip__remove" onClick={() => deleteBet(bet)} aria-label="Remove" >
                                            <IoMdClose />
                                        </button>
                                    </div>
                                    {lineTitle && <div className="props-bet-slip__single-line">{lineTitle}</div>}
                                    {matchup && <div className="props-bet-slip__single-matchup">{matchup}</div>}
                                    <div className={`props-bet-slip__line-select ${canBuyPoints ? '' : 'is-static'}`}>
                                        {pointLabel ? (
                                            <span className="props-bet-slip__line-points">{pointLabel}</span>
                                        ) : null}
                                        <span className="props-bet-slip__line-price">{formatOdds(bet.odd?.price)}</span>
                                        {canBuyPoints && (
                                            <select
                                                className="props-bet-slip__line-native"
                                                value={bet.odd?.id}
                                                aria-label="Buy points"
                                                onChange={(event) => {
                                                    const next = oddsList.find(item => item.id === event.target.value);
                                                    if (next) applyOdd(next);
                                                }}
                                            >
                                                {[...oddsList].sort((a, b) => Number(a.points ?? 0) - Number(b.points ?? 0)).map(item => (
                                                    <option key={item.id} value={item.id}>
                                                        {formatBetLine(item, bet.marketName)}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                    <div className="props-bet-slip__stake-row">
                                        <span className="props-bet-slip__currency">$</span>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            className="props-bet-slip__stake-input"
                                            value={bet.bet ? String(bet.bet) : ''}
                                            onChange={(e) => updateSingleStake(bet, e.target.value)}
                                            placeholder="0.00"
                                        />
                                    </div>
                                    <div className="props-bet-slip__return">
                                        To Win: {Number(bet.bet) > 0 ? `$${formatCurrency(bet.reward ?? 0)}` : '$0.00'}
                                    </div>
                                </div>
                            );
                        })}

                        {(user?.freePlay ?? 0) > 0 && (
                            <label className="props-bet-slip__freeplay">
                                <input
                                    type="checkbox"
                                    checked={useFreePlay}
                                    onChange={() => setUseFreePlay(prev => !prev)}
                                />
                                Use freeplay (${formatCurrency(user?.freePlay)}) for singles
                            </label>
                        )}

                        {shouldShowDelayedMessage && (
                            <div className="props-bet-slip__delayed">
                                *This bet may take a little longer to grade.
                            </div>
                        )}

                        {canParlay && (
                            <div className="props-bet-slip__separate-notice" role="note">
                                Parlay&apos;s and Straight&apos;s must be placed separately
                            </div>
                        )}

                        <div className="props-bet-slip__footer">
                            <button
                                type="button"
                                className="props-bet-slip__btn props-bet-slip__btn--primary"
                                onClick={() => { void placeSingles(); }}
                                disabled={placingSingles || placingParlay || singlesWithStake.length === 0}
                            >
                                {placingSingles
                                    ? 'Please wait...'
                                    : `Place Straight Bet (${singlesWithStake.length})`}
                            </button>
                        </div>
                    </div>
                </>
            )}

        </div>
    );
};

export default PropsBetSlip;
